import { useEffect, useRef } from "react";

/**
 * Channel zapping inputs:
 *   - remote / keyboard: ChannelUp/ChannelDown, PageUp/PageDown and the
 *     media next/previous keys (never the arrows — those drive D-pad focus)
 *   - touch: a horizontal swipe across `swipeRef` (vertical swipes stay
 *     page scrolls)
 */
export function useZapGestures(
  enabled: boolean,
  onNext: () => void,
  onPrev: () => void,
  swipeRef?: React.RefObject<HTMLElement | null>
): void {
  const handlers = useRef({ onNext, onPrev });
  handlers.current = { onNext, onPrev };

  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return;
      if (e.key === "ChannelUp" || e.key === "PageUp" || e.key === "MediaTrackNext") {
        e.preventDefault();
        handlers.current.onNext();
      } else if (e.key === "ChannelDown" || e.key === "PageDown" || e.key === "MediaTrackPrevious") {
        e.preventDefault();
        handlers.current.onPrev();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled]);

  useEffect(() => {
    const el = swipeRef?.current;
    if (!enabled || !el) return;
    let start: { x: number; y: number; t: number } | undefined;
    const onStart = (e: TouchEvent) => {
      const p = e.touches[0];
      start = e.touches.length === 1 && p ? { x: p.clientX, y: p.clientY, t: Date.now() } : undefined;
    };
    const onEnd = (e: TouchEvent) => {
      const p = e.changedTouches[0];
      if (!start || !p) return;
      const dx = p.clientX - start.x;
      const dy = p.clientY - start.y;
      const quick = Date.now() - start.t < 600;
      start = undefined;
      if (!quick || Math.abs(dx) < 70 || Math.abs(dy) > Math.abs(dx) * 0.6) return;
      if (dx < 0) handlers.current.onNext();
      else handlers.current.onPrev();
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchend", onEnd);
    };
  }, [enabled, swipeRef]);
}
